import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  CreditCard, 
  Search, 
  Filter, 
  DollarSign,
  Download,
  RefreshCw,
  Eye,
  Edit,
  MoreHorizontal,
  Users,
  Calendar,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  Crown,
  Building2,
  Mail,
  Phone,
  Globe,
  Activity,
  Zap,
  Star,
  Target,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Pause,
  Play,
  Ban,
  RotateCcw,
  Plus
} from 'lucide-react';

const UltraSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_desc');
  const [selectedSubs, setSelectedSubs] = useState([]);
  const [timeRange, setTimeRange] = useState('30d');

  const API = process.env.REACT_APP_BACKEND_URL;
  const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

  useEffect(() => { fetchSubscriptions(); }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API}/api/ultra/subscriptions`, { headers: { Authorization: `Bearer ${token}` } });
      const items = (res.data.items || res.data.subscriptions || []).map(s => {
        // Look up org name from org_id
        const orgName = s.org_name || s.org_id || '—';
        return {
          id: s.id,
          org_id: s.org_id,
          org_name: orgName,
          org_email: s.org_email || `${s.org_id}@expostand.com`,
          org_phone: s.org_phone || '—',
          user_count: s.user_count || 1,
          role: s.role || 'UNKNOWN',
          plan_code: s.plan_code || 'dynamic',
          plan_name: s.plan_name || s.plan_code || 'Dynamic',
          status: s.status || 'active',
          billing_cycle: s.billing_cycle || 'monthly',
          current_period_start: s.current_period_start || s.created_at,
          current_period_end: s.current_period_end || s.next_billing_date,
          next_billing_date: s.next_billing_date,
          seats_used: s.seats_used || 1,
          seats_total: s.seats_total || 5,
          mrr: s.mrr || s.amount || 0,
          annual_value: (s.mrr || s.amount || 0) * 12,
          created_at: s.created_at,
          trial_end: s.trial_end,
          payment_method: s.payment_method || null,
          health_score: s.health_score || (s.status === 'active' ? 85 : 40),
          churn_risk: s.churn_risk || (s.status === 'active' ? 'low' : 'high'),
          last_activity: s.last_activity || s.updated_at || s.created_at,
          usage_stats: s.usage_stats || {},
        };
      });
      setSubscriptions(items);
    } catch (error) {
      console.error('Fetch subscriptions error:', error);
    } finally {
      setLoading(false);
    }
  };


    // Filter subscriptions
  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesSearch = search === '' ||
      (sub.org_name||'').toLowerCase().includes(search.toLowerCase()) ||
      (sub.org_email||'').toLowerCase().includes(search.toLowerCase()) ||
      (sub.plan_name||'').toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
    const matchesPlan = planFilter === 'all' || sub.plan_code === planFilter;
    return matchesSearch && matchesStatus && matchesPlan;
  });

  // Sort subscriptions
  const sortedSubscriptions = [...filteredSubscriptions].sort((a, b) => {
    switch (sortBy) {
      case 'org_asc':
        return a.org_name.localeCompare(b.org_name);
      case 'org_desc':
        return b.org_name.localeCompare(a.org_name);
      case 'created_asc':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'created_desc':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'mrr_desc':
        return b.mrr - a.mrr;
      case 'health_desc':
        return b.health_score - a.health_score;
      default:
        return 0;
    }
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Aktif', icon: CheckCircle2, color: 'bg-green-100 text-green-800' },
      trial: { label: 'Deneme', icon: Clock, color: 'bg-blue-100 text-blue-800' },
      past_due: { label: 'Gecikmiş', icon: AlertCircle, color: 'bg-yellow-100 text-yellow-800' },
      cancelled: { label: 'İptal', icon: XCircle, color: 'bg-red-100 text-red-800' },
      suspended: { label: 'Askıda', icon: Pause, color: 'bg-gray-100 text-gray-800' }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    const Icon = config.icon;
    
    return (
      <Badge className={`gap-1 ${config.color} border-0`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      'EXHIBITOR': 'bg-green-100 text-green-800',
      'CONTRACTOR': 'bg-blue-100 text-blue-800', 
      'SUPPLIER': 'bg-orange-100 text-orange-800'
    };
    
    return (
      <Badge className={`${roleColors[role]} border-0 text-xs`}>
        {role}
      </Badge>
    );
  };

  const getChurnRiskBadge = (risk) => {
    const riskConfig = {
      low: { label: 'Düşük Risk', color: 'bg-green-100 text-green-800' },
      medium: { label: 'Orta Risk', color: 'bg-yellow-100 text-yellow-800' },
      high: { label: 'Yüksek Risk', color: 'bg-red-100 text-red-800' },
      churned: { label: 'Ayrıldı', color: 'bg-gray-100 text-gray-800' }
    };
    
    const config = riskConfig[risk] || riskConfig.low;
    
    return (
      <Badge className={`${config.color} border-0 text-xs`}>
        {config.label}
      </Badge>
    );
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date)) return '—';
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Az önce';
    if (diffInHours < 24) return `${diffInHours} saat önce`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) return `${diffInDays} gün önce`;
    const diffInMonths = Math.floor(diffInDays / 30);
    return `${diffInMonths} ay önce`;
  };

  const getSubscriptionStats = () => {
    const totalMRR = subscriptions.reduce((sum, sub) => sum + sub.mrr, 0);
    const totalARR = subscriptions.reduce((sum, sub) => sum + sub.annual_value, 0);
    const activeCount = subscriptions.filter(sub => sub.status === 'active').length;
    const trialCount = subscriptions.filter(sub => sub.status === 'trial').length;
    const churnRiskCount = subscriptions.filter(sub => sub.churn_risk === 'high').length;
    const avgHealthScore = subscriptions.reduce((sum, sub) => sum + sub.health_score, 0) / subscriptions.length;
    
    return { totalMRR, totalARR, activeCount, trialCount, churnRiskCount, avgHealthScore };
  };

  const stats = getSubscriptionStats();

  const exportSubscriptionsCSV = () => {
    const headers = ['ID', 'Organizasyon', 'Email', 'Plan', 'Durum', 'MRR ($)', 'Fatura Döngüsü', 'Oluşturulma', 'Churn Riski', 'Sağlık Puanı'];
    const rows = sortedSubscriptions.map(s => [
      s.id || '',
      s.org_name || '',
      s.org_email || '',
      s.plan_name || s.plan_code || '',
      s.status || '',
      s.mrr || 0,
      s.billing_cycle || '',
      s.created_at ? new Date(s.created_at).toLocaleDateString('tr-TR') : '',
      s.churn_risk || '',
      s.health_score || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `abonelikler_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8 bg-gray-50 -m-3 sm:-m-6 p-3 sm:p-6 min-h-screen">
      {/* Enhanced Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
              Abonelik Yönetimi
            </h1>
            <p className="text-gray-600 mt-1">
              Aktif abonelikler, ödeme durumları ve churn analizi • {subscriptions.length} toplam abonelik
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="7d">Son 7 Gün</option>
              <option value="30d">Son 30 Gün</option>
              <option value="90d">Son 90 Gün</option>
              <option value="1y">Son 1 Yıl</option>
            </select>
            
            <Button variant="outline" size="sm" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Churn Analizi
            </Button>
            <Button variant="outline" size="sm" className="gap-2" onClick={exportSubscriptionsCSV} data-testid="export-subs-csv-btn">
              <Download className="h-4 w-4" />
              Rapor İndir
            </Button>
            <Button size="sm" className="gap-2 bg-green-600 hover:bg-green-700">
              <Plus className="h-4 w-4" />
              Manuel Abonelik
            </Button>
          </div>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Toplam MRR</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalMRR)}</p>
                <p className="text-xs text-green-600 flex items-center mt-1">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  Aylık yinelenen gelir
                </p>
              </div>
              <div className="p-3 rounded-full bg-gradient-to-br from-green-500 to-green-600">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Yıllık Değer (ARR)</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalARR)}</p>
                <p className="text-xs text-blue-600 flex items-center mt-1">
                  <Calendar className="h-3 w-3 mr-1" />
                  Projektif yıllık gelir
                </p>
              </div>
              <div className="p-3 rounded-full bg-gradient-to-br from-blue-500 to-blue-600">
                <BarChart3 className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Aktif Abonelikler</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeCount}</p>
                <p className="text-xs text-purple-600 flex items-center mt-1">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Ödeme yapan müşteriler
                </p>
              </div>
              <div className="p-3 rounded-full bg-gradient-to-br from-purple-500 to-purple-600">
                <Users className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Churn Riski</p>
                <p className="text-2xl font-bold text-gray-900">{stats.churnRiskCount}</p>
                <p className="text-xs text-red-600 flex items-center mt-1">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Yüksek riskli aboneler
                </p>
              </div>
              <div className="p-3 rounded-full bg-gradient-to-br from-red-500 to-red-600">
                <Target className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Advanced Filters */}
      <Card className="shadow-sm border-0">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Organizasyon, email ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-10"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 h-10"
            >
              <option value="all">Tüm Durumlar</option>
              <option value="active">Aktif</option>
              <option value="trial">Deneme</option>
              <option value="past_due">Gecikmiş</option>
              <option value="cancelled">İptal</option>
              <option value="suspended">Askıda</option>
            </select>
            
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 h-10"
            >
              <option value="all">Tüm Planlar</option>
              <option value="basic">Basic</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
              <option value="advanced">Advanced</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 h-10"
            >
              <option value="created_desc">Yeni Abonelikler</option>
              <option value="created_asc">Eski Abonelikler</option>
              <option value="org_asc">A-Z Sıralama</option>
              <option value="org_desc">Z-A Sıralama</option>
              <option value="mrr_desc">MRR'ye Göre</option>
              <option value="health_desc">Sağlık Skoruna Göre</option>
            </select>
            
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-10 gap-2">
                <Filter className="h-4 w-4" />
                Filtre
              </Button>
              <Button variant="outline" size="sm" className="h-10 gap-2">
                <RefreshCw className="h-4 w-4" />
                Yenile
              </Button>
            </div>
          </div>
          
          {search && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                "{search}" için {filteredSubscriptions.length} sonuç bulundu
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearch('')}
                className="text-gray-500 hover:text-gray-700"
              >
                Temizle
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Subscriptions Table */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="flex space-x-4">
                  <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
                  <div className="space-y-2 flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="shadow-sm border-0">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 rounded-t-xl">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CreditCard className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <span className="text-lg">Platform Abonelikleri</span>
                  <p className="text-sm text-gray-500 mt-1 font-normal">
                    {filteredSubscriptions.length} abonelik listeleniyor
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Badge variant="secondary" className="bg-green-100 text-green-700">
                  MRR: {formatCurrency(stats.totalMRR)}
                </Badge>
                <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                  Aktif: {stats.activeCount}
                </Badge>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Organizasyon</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Plan & Durum</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Ödeme</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Kullanım</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Sağlık & Risk</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">Son Aktivite</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700 text-sm">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedSubscriptions.map((subscription) => (
                    <tr key={subscription.id} className="hover:bg-green-50/30 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm ${
                            subscription.role === 'CONTRACTOR' ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                            subscription.role === 'EXHIBITOR' ? 'bg-gradient-to-br from-green-500 to-green-600' :
                            'bg-gradient-to-br from-orange-500 to-orange-600'
                          }`}>
                            {subscription.org_name.charAt(0)}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">{subscription.org_name}</div>
                            <div className="text-sm text-gray-500">{subscription.org_email}</div>
                            <div className="flex items-center gap-2 mt-1">
                              {getRoleBadge(subscription.role)}
                              <span className="text-xs text-gray-400">{subscription.user_count} kullanıcı</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-2">
                          <div className="font-medium text-gray-900">{subscription.plan_name}</div>
                          {getStatusBadge(subscription.status)}
                          <div className="text-xs text-gray-500 capitalize">
                            {subscription.billing_cycle === 'monthly' ? 'Aylık' : 'Yıllık'} faturalandırma
                          </div>
                          {subscription.trial_end && (
                            <div className="text-xs text-blue-600">
                              Deneme süresi: {subscription.trial_end && !isNaN(new Date(subscription.trial_end)) ? new Date(subscription.trial_end).toLocaleDateString('tr-TR') : '—'}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-1">
                          <div className="font-semibold text-gray-900">
                            {subscription.mrr > 0 ? formatCurrency(subscription.mrr) : 'Ücretsiz'}
                          </div>
                          <div className="text-xs text-gray-500">
                            ARR: {formatCurrency(subscription.annual_value)}
                          </div>
                          {subscription.payment_method && (
                            <div className="flex items-center text-xs text-gray-600">
                              <CreditCard className="h-3 w-3 mr-1" />
                              **** {subscription.payment_method.last4}
                            </div>
                          )}
                          {subscription.next_billing_date && (
                            <div className="text-xs text-gray-500">
                              Sonraki: {subscription.next_billing_date && !isNaN(new Date(subscription.next_billing_date)) ? new Date(subscription.next_billing_date).toLocaleDateString('tr-TR') : '—'}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-2">
                          <div className="flex items-center text-sm">
                            <Users className="h-3 w-3 mr-1 text-gray-400" />
                            <span>{subscription.seats_used}</span>
                            {subscription.seats_total > 0 && (
                              <span className="text-gray-400">/{subscription.seats_total}</span>
                            )}
                            {subscription.seats_total === -1 && (
                              <span className="text-gray-400">/∞</span>
                            )}
                          </div>
                          
                          {subscription.usage_stats.storage_used_gb && (
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-blue-500 h-2 rounded-full"
                                style={{ 
                                  width: `${Math.min((subscription.usage_stats.storage_used_gb / subscription.usage_stats.storage_limit_gb) * 100, 100)}%` 
                                }}
                              />
                            </div>
                          )}
                          
                          <div className="text-xs text-gray-500">
                            {subscription.usage_stats.storage_used_gb?.toFixed(1)}GB / 
                            {subscription.usage_stats.storage_limit_gb}GB depolama
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <div className="flex items-center space-x-1">
                              <Activity className="h-4 w-4 text-blue-500" />
                              <span className="font-medium text-gray-900">{subscription.health_score}</span>
                              <span className="text-xs text-gray-500">/100</span>
                            </div>
                          </div>
                          
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                subscription.health_score >= 80 ? 'bg-green-500' :
                                subscription.health_score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${subscription.health_score}%` }}
                            />
                          </div>
                          
                          {getChurnRiskBadge(subscription.churn_risk)}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-sm text-gray-700">
                          {formatTimeAgo(subscription.last_activity)}
                        </div>
                        <div className="text-xs text-gray-500">
                          Üyelik: {subscription.created_at && !isNaN(new Date(subscription.created_at)) ? new Date(subscription.created_at).toLocaleDateString('tr-TR') : '—'}
                        </div>
                        {subscription.status === 'cancelled' && subscription.cancelled_at && (
                          <div className="text-xs text-red-600 mt-1">
                            İptal: {subscription.cancelled_at && !isNaN(new Date(subscription.cancelled_at)) ? new Date(subscription.cancelled_at).toLocaleDateString('tr-TR') : '—'}
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-2">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-green-100">
                            <Eye className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-blue-100">
                            <Edit className="h-4 w-4 text-blue-600" />
                          </Button>
                          {subscription.status === 'active' && (
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-yellow-100">
                              <Pause className="h-4 w-4 text-yellow-600" />
                            </Button>
                          )}
                          {subscription.status === 'cancelled' && (
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-green-100">
                              <RotateCcw className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-gray-100">
                            <MoreHorizontal className="h-4 w-4 text-gray-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/30">
              <div className="text-sm text-gray-500">
                Toplam {subscriptions.length} abonelikten {filteredSubscriptions.length} tanesi gösteriliyor
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" disabled>
                  Önceki
                </Button>
                <Button variant="outline" size="sm" className="bg-green-600 text-white border-green-600">
                  1
                </Button>
                <Button variant="outline" size="sm">
                  2
                </Button>
                <Button variant="outline" size="sm">
                  Sonraki
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UltraSubscriptions;